import { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Facebook, Instagram, ShoppingBag, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/lib/cart";
import { usePageSettings } from "@/lib/page-settings";
import { PURCHASING_ENABLED } from "@/lib/purchasing";
import { SearchDropdown } from "@/components/common/SearchDropdown";
import { ThreadsIcon } from "@/components/common/ThreadsIcon";
import { TikTokIcon } from "@/components/common/TikTokIcon";

// Fallback nav — used only until page settings resolve on first load
// (or as the default on API error, via PageSettingsProvider).
const FALLBACK_LINKS = [
  { to: "/", label: "Home" },
  { to: "/artists", label: "Artists" },
  { to: "/events", label: "Events" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fine-art-frame/artifacts/ygkfc1mx_Untitled%20design%20(42).png";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const { itemCount, setDrawerOpen } = useCart();
  const { status, pages } = usePageSettings();

  // Build the nav link list from admin-controlled settings. Only render
  // visible pages, sorted by display_order. Fall back to the hardcoded
  // list while settings load or if the API errored.
  const links = useMemo(() => {
    if (status === "loading") return FALLBACK_LINKS;
    if (!Array.isArray(pages) || pages.length === 0) return FALLBACK_LINKS;
    return pages
      .filter((p) => p.is_visible && p.route)
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
      .map((p) => ({ to: p.route, label: p.page_name }));
  }, [status, pages]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  const isHome = location.pathname === "/";
  const showSolid = !isHome || scrolled;

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        showSolid
          ? "bg-white/95 backdrop-blur-xl shadow-[0_1px_0_rgba(18,33,46,0.08)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center"
        >
          <img
            src={LOGO_URL}
            alt="Rain Boy Fine Art"
            className={`h-10 transition-all duration-500 ${showSolid ? "" : "brightness-0 invert"}`}
          />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className={`nav-link text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                location.pathname === link.to
                  ? showSolid ? "text-[#0A1128] font-medium" : "text-white font-medium"
                  : showSolid ? "text-[#0A1128]/70 hover:text-[#0A1128]" : "text-white/70 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}

          {/* Social Icons */}
          <div className={`flex items-center gap-3 ml-4 border-l pl-4 transition-colors duration-300 ${showSolid ? "border-[#0A1128]/10" : "border-white/20"}`}>
            <a
              href="https://www.facebook.com/profile.php?id=61581586250780"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-facebook"
              className={`transition-colors duration-300 ${showSolid ? "text-[#0A1128] hover:text-[#CBA365]" : "text-white/70 hover:text-white"}`}
            >
              <Facebook size={15} />
            </a>
            <a
              href="https://www.instagram.com/rainboyfineart"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              className={`transition-colors duration-300 ${showSolid ? "text-[#0A1128] hover:text-[#CBA365]" : "text-white/70 hover:text-white"}`}
            >
              <Instagram size={15} />
            </a>
            <a
              href="https://www.threads.com/@rainboyfineart"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-threads"
              className={`transition-colors duration-300 ${showSolid ? "text-[#0A1128] hover:text-[#CBA365]" : "text-white/70 hover:text-white"}`}
            >
              <ThreadsIcon size={14} />
            </a>
            <a
              href="https://www.tiktok.com/@rainboyfineart"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-tiktok"
              className={`transition-colors duration-300 ${showSolid ? "text-[#0A1128] hover:text-[#CBA365]" : "text-white/70 hover:text-white"}`}
            >
              <TikTokIcon size={14} />
            </a>
          </div>

          {/* Search + Cart */}
          <div className={`relative flex items-center gap-3 border-l pl-4 transition-colors duration-300 ${showSolid ? "border-[#0A1128]/10" : "border-white/20"}`}>
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              data-testid="nav-search-button"
              className={`transition-colors duration-300 ${showSolid ? "text-[#0A1128] hover:text-[#CBA365]" : "text-white/70 hover:text-white"}`}
            >
              <Search size={16} />
            </button>
            {PURCHASING_ENABLED && (
              <button
                onClick={() => setDrawerOpen(true)}
                data-testid="nav-cart-button"
                className={`relative transition-colors duration-300 ${showSolid ? "text-[#0A1128] hover:text-[#CBA365]" : "text-white/70 hover:text-white"}`}
              >
                <ShoppingBag size={16} />
                {itemCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-[#CBA365] text-white text-[9px] rounded-full flex items-center justify-center leading-none" data-testid="nav-cart-count">
                    {itemCount}
                  </span>
                )}
              </button>
            )}

            <SearchDropdown isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
          </div>
        </div>

        {/* Mobile toggle */}
        <div className="flex items-center gap-3 md:hidden">
          <button
            onClick={() => setSearchOpen(!searchOpen)}
            data-testid="mobile-search-button"
            className={`p-2 transition-colors duration-300 ${showSolid ? "text-[#0A1128]" : "text-white"}`}
          >
            <Search size={20} />
          </button>
          {PURCHASING_ENABLED && (
            <button
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-cart-button"
              className={`p-2 relative transition-colors duration-300 ${showSolid ? "text-[#0A1128]" : "text-white"}`}
            >
              <ShoppingBag size={20} />
              {itemCount > 0 && (
                <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-[#CBA365] text-white text-[9px] rounded-full flex items-center justify-center leading-none">
                  {itemCount}
                </span>
              )}
            </button>
          )}
          <button
            data-testid="mobile-menu-toggle"
            className={`p-2 transition-colors duration-300 ${showSolid ? "text-[#0A1128]" : "text-white"}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="md:hidden bg-white border-t border-[#0A1128]/10 overflow-hidden"
          >
            <div className="px-6 py-8 space-y-5">
              {links.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.15 + i * 0.1, ease: "easeOut" }}
                >
                  <Link
                    to={link.to}
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    className="block text-sm uppercase tracking-[0.2em] text-[#0A1128] hover:text-[#CBA365] transition-colors"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.15 + links.length * 0.1 }}
                className="flex items-center gap-4 pt-4 border-t border-[#0A1128]/10"
              >
                <a href="https://www.facebook.com/profile.php?id=61581586250780" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6]">
                  <Facebook size={18} />
                </a>
                <a href="https://www.instagram.com/rainboyfineart" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6]">
                  <Instagram size={18} />
                </a>
                <a href="https://www.threads.com/@rainboyfineart" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6]">
                  <ThreadsIcon size={16} />
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
